﻿import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, type ResolutionDocumentRow } from "../app/api";
import { applyUiFrameworkMode } from "../app/ui-framework";
import { loadUiSettings, type OwnerThemeSettings } from "../app/ui-settings";
import { formatUnitTypeLabel } from "../shared/unitTypes";

const ownerPageBackgroundUrl = new URL("../assets/Hintergrundgrafik.png", import.meta.url).toString();

const formatDateTimeDE = (value?: string) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(d);
};

const formatDateDE = (value?: string) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  }).format(d);
};

const formatUnitNumber4Digits = (value?: string) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "-";
  if (/^\d+$/.test(raw)) return raw.padStart(4, "0");
  return raw;
};

const formatStatus = (status: "entwurf" | "aktiv" | "abgeschlossen") => {
  if (status === "aktiv") return "l\u00e4uft";
  if (status === "abgeschlossen") return "beendet";
  return "Entwurf";
};

const formatQuorum = (quorum: "einfache_mehrheit" | "qualifizierte_mehrheit" | "allstimmig") => {
  if (quorum === "qualifizierte_mehrheit") return "Qualifizierte Mehrheit";
  if (quorum === "allstimmig") return "Allstimmig";
  return "Einfache Mehrheit";
};

const formatPower = (value: number) => {
  if (!Number.isFinite(value)) return "0";
  if (Math.abs(value % 1) < 0.00001) return String(Math.round(value));
  return value.toFixed(2).replace(".", ",");
};

const voteLabel = (value: "ja" | "nein" | "enthaltung" | "offen") => {
  if (value === "ja") return "Ja";
  if (value === "nein") return "Nein";
  if (value === "enthaltung") return "Enthaltung";
  return "Offen";
};

const voteBadgeClass = (value: "ja" | "nein" | "enthaltung" | "offen") => {

  if (value === "ja") return "vote-ja";
  if (value === "nein") return "vote-nein";
  if (value === "enthaltung") return "vote-enthaltung";
  return "vote-offen";
};

const OwnerResultDonut = ({ ja, nein, enthaltung }: { ja: number; nein: number; enthaltung: number }) => {
  const bewerteteSumme = Math.max(ja + nein, 0);
  const gesamtMitEnthaltung = Math.max(ja + nein + enthaltung, 0);
  const jaPct = bewerteteSumme > 0 ? (ja / bewerteteSumme) * 100 : 0;
  const neinPct = bewerteteSumme > 0 ? (nein / bewerteteSumme) * 100 : 0;

  const jaSegment = gesamtMitEnthaltung > 0 ? (ja / gesamtMitEnthaltung) * 100 : 0;
  const neinSegment = gesamtMitEnthaltung > 0 ? (nein / gesamtMitEnthaltung) * 100 : 0;
  const p1 = jaSegment;
  const p2 = jaSegment + neinSegment;
  const gradient =
    gesamtMitEnthaltung > 0
      ? `conic-gradient(#2f7d32 0% ${p1}%, #a12b2b ${p1}% ${p2}%, #e0b000 ${p2}% 100%)`
      : "conic-gradient(#e5e9ee 0% 100%)";

  return (
    <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
      <div
        aria-label="Stimmen Kreisdiagramm"
        title="Stimmenverteilung"
        style={{
          width: 156,
          height: 156,
          borderRadius: "50%",
          background: gradient,
          position: "relative",
          border: "1px solid var(--line)"
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 38,
            borderRadius: "50%",
            background: "var(--surface)",
            border: "1px solid var(--line)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            color: "var(--text-soft)",
            textAlign: "center",
            padding: 6,
            whiteSpace: "pre-line"
          }}
        >
          {gesamtMitEnthaltung > 0 ? `${gesamtMitEnthaltung}` + "\nStimmen" : "Keine\nStimmen"}
        </div>
      </div>
      <div style={{ display: "grid", gap: 6, minWidth: 190 }}>
        <div style={{ display: "grid", gridTemplateColumns: "120px auto", alignItems: "center", columnGap: 10 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: "50%", background: "#2f7d32" }} />Ja</span>
          <strong>{ja.toFixed(2)} ({jaPct.toFixed(1)} %)</strong>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "120px auto", alignItems: "center", columnGap: 10 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: "50%", background: "#a12b2b" }} />Nein</span>
          <strong>{nein.toFixed(2)} ({neinPct.toFixed(1)} %)</strong>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "120px auto", alignItems: "center", columnGap: 10 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: "50%", background: "#e0b000" }} />Enthaltung</span>
          <strong>{enthaltung.toFixed(2)} (ohne Bewertung)</strong>
        </div>
      </div>
    </div>
  );
};

const isPreviewableOwnerDocument = (doc: ResolutionDocumentRow) => {
  const mime = (doc.mimeType ?? "").toLowerCase();
  if (mime === "application/pdf" || mime.startsWith("image/")) return true;
  const name = String(doc.originalName ?? "").trim().toLowerCase();
  if (name.endsWith(".pdf")) return true;
  if (name.endsWith(".png") || name.endsWith(".jpg") || name.endsWith(".jpeg") || name.endsWith(".gif") || name.endsWith(".webp") || name.endsWith(".svg")) return true;
  const fileUrl = String(doc.fileUrl ?? "").toLowerCase();
  if (fileUrl.includes(".pdf")) return true;
  return false;
};
type OwnerBranding = {
  firmenname?: string;
  logoDataUrl?: string;
  uiFramework?: "classic" | "mockup_v2";
  theme: OwnerThemeSettings;
};

type VotingPowerSummary = {
  title: string;
  text: string;
  own: number;
  total: number;
  unitLabel: string;
  lines: string[];
};

const loadOwnerBranding = (): OwnerBranding => {
  const ui = loadUiSettings();
  return {
    firmenname: ui.management.firmenname,
    logoDataUrl: ui.ownerLogoDataUrl,
    uiFramework: ui.management.uiFramework,
    theme: ui.ownerTheme
  };
};

const ownerThemeVars = (theme: OwnerThemeSettings): CSSProperties =>
  ({
    "--bg": theme.bg,
    "--surface": theme.surface,
    "--line": theme.line,
    "--text": theme.text,
    "--primary": theme.primary,
    "--owner-gradient-from": theme.gradientFrom,
    "--owner-gradient-to": theme.gradientTo,
    "--owner-gradient-enabled": theme.gradientEnabled ? "1" : "0",
    "--owner-gradient-strength": theme.gradientEnabled ? "100%" : "0%",
    "--owner-page-bg-image-enabled": theme.ownerPageBackgroundImageEnabled ? "1" : "0",
    "--owner-page-bg-image": `url("${ownerPageBackgroundUrl}")`
  }) as CSSProperties;

export const OwnerVotingPage = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const isLoginMode = searchParams.get("mode") === "login";
  const localBranding = useMemo(() => loadOwnerBranding(), []);
  const brandingQuery = useQuery({
    queryKey: ["ui-settings-public"],
    queryFn: () => api.uiBranding(),
    retry: 1,
    staleTime: 60_000
  });
  const branding = useMemo(() => {
    const ui = brandingQuery.data;
    if (!ui) return localBranding;
    return {
      firmenname: ui.management.firmenname,
      logoDataUrl: ui.ownerLogoDataUrl,
      uiFramework: ui.management.uiFramework,
      theme: ui.ownerTheme
    };
  }, [brandingQuery.data, localBranding]);

  useEffect(() => {
    applyUiFrameworkMode(branding.uiFramework === "mockup_v2" ? "mockup_v2" : "classic");
  }, [branding.uiFramework]);

  useEffect(() => {
    document.title = "ObjektVote | Abstimmen";
  }, []);

  const [voteValue, setVoteValue] = useState<"ja" | "nein" | "enthaltung">("ja");
  const [comment, setComment] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [previewDocument, setPreviewDocument] = useState<ResolutionDocumentRow | null>(null);
  const [suppressClosedResultUntilReload, setSuppressClosedResultUntilReload] = useState(false);

  useEffect(() => {
    setSuppressClosedResultUntilReload(false);
  }, [token]);

  const accessQuery = useQuery({
    queryKey: ["owner-voting-access", token],
    queryFn: () => api.ownerVotingAccess(token as string),
    enabled: !!token
  });

  const resultQuery = useQuery({
    queryKey: ["owner-voting-result", token],
    enabled: !!token,
    queryFn: () => api.ownerVotingResult(token as string)
  });

  const votingPowerSummary = useMemo((): VotingPowerSummary | null => {
    const access = accessQuery.data;
    if (!access) return null;

    const art = access.resolution.stimmrechtsart;
    const accessAssignments = access.assignments ?? [];
    const resultTotal = Number(resultQuery.data?.stimmen?.gesamt ?? 0);

    const unitsByNumber = new Map(
      accessAssignments
        .map((assignment) => [String(assignment.einheitsnummer ?? "").trim(), assignment] as const)
        .filter(([nr]) => Boolean(nr))
    );
    const ownerUnitNumbers = Array.from(
      new Set([
        ...accessAssignments.map((a) => String(a.einheitsnummer ?? "").trim()).filter(Boolean)
      ])
    );

    const accessStimmrechtForOwner = accessAssignments.reduce((sum, a) => sum + (a.stimmrecht ?? 0), 0);
    const formatEinheitTyp = (raw?: string) => {
      const label = formatUnitTypeLabel(raw);
      return label && label !== "-" ? label : "Einheit";
    };
    const accessMeaForOwner = accessAssignments.reduce((sum, a) => sum + (a.mea ?? 0), 0);
    const unitsStimmrechtForOwner = ownerUnitNumbers.reduce((sum, nr) => sum + (unitsByNumber.get(nr)?.stimmrecht ?? 0), 0);
    const unitsMeaForOwner = ownerUnitNumbers.reduce((sum, nr) => sum + (unitsByNumber.get(nr)?.mea ?? 0), 0);

    if (art === "kopf") {
      const own = ownerUnitNumbers.length > 0 ? 1 : 0;
      const total = resultTotal || own;

      return {
        title: "Stimmanteile: Kopfprinzip",
        text: "1 Stimme pro Kopf (auch bei mehreren Wohnungen/Einheiten).",
        own,
        total,
        unitLabel: "K\u00f6pfe",
        lines: ownerUnitNumbers.map((nr) => {
          const unit = unitsByNumber.get(nr);
          return `${formatEinheitTyp(unit?.einheitstyp)} Nr. ${nr}`;
        })
      };
    }

    if (art === "stimmrecht") {
      const own = accessStimmrechtForOwner || unitsStimmrechtForOwner;
      const total = resultTotal || own;

      return {
        title: "Stimmanteile: Stimmen pro Einheit",
        text: "Die Stimmanteile ergeben sich aus den stimmberechtigten Einheiten und deren Stimmrechten.",
        own,
        total,
        unitLabel: "Stimmen",
        lines: ownerUnitNumbers.map((nr) => {
          const unit = unitsByNumber.get(nr);
          const fromAssignment = accessAssignments.find((a) => a.einheitsnummer === nr)?.stimmrecht ?? 0;
          const stimm = unit?.stimmrecht ?? fromAssignment;
          return `${formatEinheitTyp(unit?.einheitstyp)} Nr. ${nr}: ${formatPower(stimm)} Stimme(n)`;
        })
      };
    }

    const own = accessMeaForOwner || unitsMeaForOwner;
    const total = resultTotal || own;

    return {
      title: "Stimmanteile: Miteigentumsanteile (MEA)",
      text: "Die Stimmanteile ergibt sich aus den stimmberechtigten Einheiten und deren MEA.",
      own,
      total,
      unitLabel: "MEA",
      lines: ownerUnitNumbers.map((nr) => {
        const unit = unitsByNumber.get(nr);
        const fromAssignment = accessAssignments.find((a) => a.einheitsnummer === nr)?.mea ?? 0;
        const mea = unit?.mea ?? fromAssignment;
        return `${formatEinheitTyp(unit?.einheitstyp)} Nr. ${nr}: ${formatPower(mea)} MEA`;
      })
    };
  }, [
    accessQuery.data,
    resultQuery.data?.stimmen?.gesamt
  ]);

  const currentVote = accessQuery.data?.current_vote;
  const voteReceiptMessage = currentVote
    ? currentVote.voteValue === "ja"
      ? "Ihre JA-Stimme wurde am " + formatDateTimeDE(currentVote.voteReceivedAt) + " verbindlich erfasst."
      : currentVote.voteValue === "nein"
        ? "Ihre NEIN-Stimme wurde am " + formatDateTimeDE(currentVote.voteReceivedAt) + " verbindlich erfasst."
        : "Ihre Enthaltung wurde am " + formatDateTimeDE(currentVote.voteReceivedAt) + " verbindlich erfasst."
    : "";
  const voteReceiptClassName = currentVote
    ? currentVote.voteValue === "ja"
      ? "owner-vote-success-notice owner-vote-success-notice--ja"
      : currentVote.voteValue === "nein"
        ? "owner-vote-success-notice owner-vote-success-notice--nein"
        : "owner-vote-success-notice owner-vote-success-notice--enthaltung"
    : "owner-vote-success-notice";
  const voteReceiptIconClassName = currentVote
    ? currentVote.voteValue === "ja"
      ? "owner-vote-success-icon owner-vote-success-icon--ja"
      : currentVote.voteValue === "nein"
        ? "owner-vote-success-icon owner-vote-success-icon--nein"
        : "owner-vote-success-icon owner-vote-success-icon--enthaltung"
    : "owner-vote-success-icon";
  const currentReturnRateText = (() => {
    const abgegeben = Number(resultQuery.data?.stimmen.abgegeben ?? 0);
    const gesamt = Number(resultQuery.data?.stimmen.gesamt ?? 0);
    if (gesamt <= 0) return "Derzeitige Rücklaufquote: -";
    const quote = (abgegeben / gesamt) * 100;
    return `Derzeitige Rücklaufquote: ${quote.toFixed(2).replace(".", ",")} % (${formatPower(abgegeben)} von ${formatPower(gesamt)} Stimmen).`;
  })();


  const documentsQuery = useQuery({
    queryKey: ["owner-voting-documents", token],
    enabled: !!token,
    queryFn: () => api.ownerVotingDocuments(token as string)
  });

  const submitMutation = useMutation({
    mutationFn: () =>
      api.ownerVotingSubmit(token as string, {
        voteValue,
        comment: comment.trim() ? comment.trim() : undefined,
        auth_mode: isLoginMode ? "beschluss_id_passwort" : "beschluss_link"
      }),
    onSuccess: async () => {
      setSubmitError("");
      setSuppressClosedResultUntilReload(true);
      await Promise.all([accessQuery.refetch(), resultQuery.refetch()]);
    },
    onError: async (e) => {
      const message = e instanceof Error ? e.message : "Stimme konnte nicht gespeichert werden";
      const normalized = message.toLowerCase();
      if (normalized.includes("bereits") && normalized.includes("stimm")) {
        setSubmitError("");
        await Promise.all([accessQuery.refetch(), resultQuery.refetch()]);
        return;
      }
      setSubmitError(message);
    }
  });

  const canVote = accessQuery.data?.access.can_vote ?? false;
  const isDraft = accessQuery.data?.resolution.status === "entwurf";
  const isClosed = accessQuery.data?.resolution.status === "abgeschlossen";

  const ownerResultVisible = Boolean(resultQuery.data?.owner_einzelstimmabgabe_sichtbar ?? accessQuery.data?.resolution.owner_einzelstimmabgabe_sichtbar);
  const ownerResultRows = resultQuery.data?.einzelstimmabgaben ?? [];
  const quorumHinweis = resultQuery.data?.quorum_hinweis?.trim() ?? "";
  const resultLabel = resultQuery.data?.ergebnis ?? "-";
  const resultLabelLower = resultLabel.toLowerCase();
  const isResultAccepted = resultLabelLower.includes("angenommen");
  const isResultRejected = resultLabelLower.includes("abgelehnt");
  const resultOutcomeText = resultLabelLower.includes("angenommen")
    ? "Beschluss wurde angenommen"
    : resultLabelLower.includes("abgelehnt")
      ? "Beschluss wurde abgelehnt"
      : `Ergebnis: ${resultLabel}`;
  const resultOutcomeClass = isResultAccepted
    ? "owner-vote-result-outcome--ok"
    : isResultRejected
      ? "owner-vote-result-outcome--fail"
      : "owner-vote-result-outcome--neutral";
  const resultOutcomeCardClass = isResultAccepted
    ? "owner-vote-result-status-card--ok"
    : isResultRejected
      ? "owner-vote-result-status-card--fail"
      : "owner-vote-result-status-card--neutral";

  const ownerDocumentRows = documentsQuery.data?.data ?? [];

  const submitLabel =
    voteValue === "ja"
      ? "Ja - Stimme verbindlich abgeben"
      : voteValue === "nein"
        ? "Nein - Stimme verbindlich abgeben"
        : "Enthaltung verbindlich abgeben";

  const submitVariantClass =
    voteValue === "ja"
      ? "owner-vote-submit-ja"
      : voteValue === "nein"
        ? "owner-vote-submit-nein"
        : "owner-vote-submit-enthaltung";

  const statusBadgeClass = useMemo(() => {
    const status = accessQuery.data?.resolution.status;
    if (status === "aktiv") return "badge aktiv";
    if (status === "abgeschlossen") return "badge inaktiv";
    return "badge ergebnis-neutral";
  }, [accessQuery.data?.resolution.status]);

  if (!token) {
    return (
    <main
      className={`owner-vote-shell${branding.theme.ownerPageBackgroundImageEnabled ? " owner-vote-shell--bg-on" : ""}`}
      style={ownerThemeVars(branding.theme)}
    >
      <section className="panel owner-vote-card">{"Ung\u00fcltiger Zugangslink."}</section>
    </main>
  );
}

  return (
    <main
      className={`owner-vote-shell${branding.theme.ownerPageBackgroundImageEnabled ? " owner-vote-shell--bg-on" : ""}`}
      style={ownerThemeVars(branding.theme)}
    >
      <section className="panel owner-vote-card owner-vote-card--mockup">
        {accessQuery.isLoading ? <div>Daten werden geladen...</div> : null}
        {accessQuery.isError ? <div className="error-text">{accessQuery.error instanceof Error ? accessQuery.error.message : "Fehler"}</div> : null}

        {accessQuery.data ? (
          <>
            <div className="owner-vote-toprow">
              <header className="owner-vote-header">
                <div className="owner-auth-kicker owner-vote-kicker">
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M6 3.8h8.8l3.2 3.2V20a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4.8a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M14.8 3.8v3.2H18" stroke="currentColor" strokeWidth="1.8" />
                    <path d="m8.3 13.4 2.2 2.2 4.2-4.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Digitale Abstimmung</span>
                </div>
                <h1 className="owner-vote-title">{accessQuery.data.resolution.titel}</h1>
                <div className="owner-vote-subline">
                  <strong className="owner-vote-number">{accessQuery.data.resolution.beschlussnummer}</strong>
                  <span className={statusBadgeClass}>{formatStatus(accessQuery.data.resolution.status)}</span>
                </div>
              </header>
              {branding.logoDataUrl ? (
                <div className="owner-vote-logo-wrap">
                  <img src={branding.logoDataUrl} alt={branding.firmenname ? `${branding.firmenname} Logo` : "Firmenlogo"} className="owner-auth-logo owner-vote-logo" />
                </div>
              ) : null}
            </div>

            <section className="owner-vote-meta-grid">
              <div className="owner-vote-meta-item">
                <span className="owner-vote-meta-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M12 21s6-5.7 6-10a6 6 0 1 0-12 0c0 4.3 6 10 6 10Z" stroke="currentColor" strokeWidth="1.8" />
                    <circle cx="12" cy="11" r="2.2" stroke="currentColor" strokeWidth="1.8" />
                  </svg>
                </span>
                <div className="owner-vote-meta-copy">
                  <span>Objekt</span>
                  <strong>
                    {accessQuery.data.resolution.objektnummer} | {accessQuery.data.resolution.objektname}
                  </strong>
                </div>
              </div>
              <div className="owner-vote-meta-item">
                <span className="owner-vote-meta-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="8.3" r="3.2" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M5.5 19.5a6.5 6.5 0 0 1 13 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </span>
                <div className="owner-vote-meta-copy">
                  <span>{"Eigent\u00fcmer"}</span>
                  <strong>{accessQuery.data.owner.name}</strong>
                </div>
              </div>
              <div className="owner-vote-meta-item">
                <span className="owner-vote-meta-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="4" y="5.4" width="16" height="14.6" rx="2" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M8 3.8v3.2M16 3.8v3.2M4 9.6h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </span>
                <div className="owner-vote-meta-copy">
                  <span>Abstimmungsende</span>
                  <strong>{formatDateDE(accessQuery.data.resolution.endedatum)}</strong>
                </div>
              </div>
              <div className="owner-vote-meta-item">
                <span className="owner-vote-meta-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M12 3.5 5 6v5.4c0 4.3 2.7 8.2 7 9.8 4.3-1.6 7-5.5 7-9.8V6l-7-2.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="m9.2 12.2 1.8 1.8 3.6-3.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <div className="owner-vote-meta-copy">
                  <span>Quorum</span>
                  <strong>{formatQuorum(accessQuery.data.resolution.quorum)}</strong>
                  {quorumHinweis ? <div className="owner-vote-meta-note">{quorumHinweis}</div> : null}
                </div>
              </div>
            </section>

            {votingPowerSummary ? (
              <section className="owner-vote-power">
                <div className="owner-vote-power-card">
                  <div className="owner-vote-section-title owner-vote-power-title">{votingPowerSummary.title}</div>
                  <p className="owner-vote-power-text">{votingPowerSummary.text}</p>
                  <div className="owner-vote-power-metrics">
                    <div className="owner-vote-power-metric">
                      <span className="owner-vote-power-metric-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none">
                          <circle cx="8.2" cy="8.5" r="2.7" stroke="currentColor" strokeWidth="1.8" />
                          <circle cx="15.8" cy="9.1" r="2.3" stroke="currentColor" strokeWidth="1.8" />
                          <path d="M3.8 18a4.6 4.6 0 0 1 8.8 0M12 18a3.8 3.8 0 0 1 7.2 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                      </span>
                      <div className="owner-vote-power-metric-copy">
                        <span>Ihre Stimmanteile</span>
                        <strong>
                          {formatPower(votingPowerSummary.own)} {votingPowerSummary.unitLabel === "K\u00f6pfe" ? "Kopf" : votingPowerSummary.unitLabel}
                        </strong>
                      </div>
                      {votingPowerSummary.lines.length ? (
                        <div className="owner-vote-power-inline-list">
                          {votingPowerSummary.lines.map((line) => (
                            <div key={line} className="owner-vote-power-inline-item">{line}</div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div className="owner-vote-power-metric">
                      <span className="owner-vote-power-metric-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none">
                          <path d="M12 4v5.8h5.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M12 4a8 8 0 0 1 8 8h-8V4Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                          <path d="M12 4a8 8 0 0 0-5.7 13.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                          <circle cx="6.7" cy="16.6" r="1.8" stroke="currentColor" strokeWidth="1.8" />
                          <circle cx="12" cy="16.1" r="2.1" stroke="currentColor" strokeWidth="1.8" />
                          <circle cx="17.3" cy="16.6" r="1.8" stroke="currentColor" strokeWidth="1.8" />
                          <path d="M3.9 21v-1a3 3 0 0 1 5.8 0v1M9.1 21v-1.1a3.4 3.4 0 0 1 5.8 0V21M14.3 21v-1a3 3 0 0 1 5.8 0v1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                      </span>
                      <div className="owner-vote-power-metric-copy">
                        <span>Gesamtstimmanteile</span>
                        <strong>
                          {formatPower(votingPowerSummary.total)} {votingPowerSummary.unitLabel}
                        </strong>
                      </div>
                    </div>
                    <div className="owner-vote-power-metric">
                      <span className="owner-vote-power-metric-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none">
                          <path d="M12 4.2a7.8 7.8 0 1 0 7.8 7.8H12V4.2Z" stroke="currentColor" strokeWidth="1.8" />
                          <path d="M14.2 3.5a7.9 7.9 0 0 1 6.3 6.3h-6.3V3.5Z" stroke="currentColor" strokeWidth="1.8" />
                        </svg>
                      </span>
                      <div className="owner-vote-power-metric-copy">
                        <span>Ihre Stimmmacht</span>
                        <strong>
                          {formatPower(votingPowerSummary.own)} / {formatPower(votingPowerSummary.total)} {votingPowerSummary.unitLabel}
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            ) : null}

            <section className="owner-vote-proposal">
              <div className="owner-vote-proposal-title">{accessQuery.data.resolution.titel}</div>
              <div className="owner-vote-proposal-request">Beschlussantrag:</div>
              <div className="owner-vote-proposal-text">{accessQuery.data.resolution.beschlusstext}</div>
            </section>

            {ownerDocumentRows.length ? (
              <section className="owner-vote-proposal owner-vote-documents" style={{ marginTop: 10 }}>
                <details className="owner-vote-documents-details">
                  <summary className="owner-vote-documents-summary">
                    <span className="owner-vote-documents-bullet" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M6 4.4h8.6L18 7.8V19a1 1 0 0 1-1 1H6.9a1 1 0 0 1-1-1V5.4a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.7" />
                        <path d="M14.6 4.4v3.4H18" stroke="currentColor" strokeWidth="1.7" />
                        <path d="M8.6 11h6.8M8.6 14.2h6.8M8.6 17.4h4.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                      </svg>
                    </span>
                    <span className="owner-vote-documents-label">
                      <strong>Dokumente zum Beschluss</strong>
                    </span>
                  </summary>
                  <div className="owner-vote-documents-body">
                    <div style={{ overflowX: "auto" }}>
                      <table>
                        <thead>
                          <tr>
                            <th>Datei</th>
                            <th style={{ textAlign: "center" }}>Aktion</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ownerDocumentRows.map((doc) => (
                            <tr key={doc.id}>
                              <td>{doc.originalName}</td>
                              <td style={{ textAlign: "center" }}><button type="button" onClick={() => setPreviewDocument(doc)}>Öffnen</button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </details>
              </section>
            ) : null}

            {previewDocument ? (
              <div className="modal-backdrop">
                <section
                  className="panel modal-card"
                  style={{
                    width: isPreviewableOwnerDocument(previewDocument) ? "min(1400px, 98vw)" : "min(620px, 92vw)",
                    maxWidth: isPreviewableOwnerDocument(previewDocument) ? 1400 : 620,
                    maxHeight: "94vh",
                    overflow: "auto"
                  }}
                >
                  <h3 style={{ marginTop: 0, marginBottom: 8 }}>{previewDocument.originalName}</h3>
                  <div style={{ marginBottom: 10 }}>
                    {isPreviewableOwnerDocument(previewDocument) ? (
                      <iframe title={previewDocument.originalName} src={previewDocument.fileUrl} style={{ width: "100%", height: "80vh", border: "1px solid var(--line)", borderRadius: 6 }} />
                    ) : (
                      <div style={{ color: "var(--text-soft)" }}>Für diesen Dateityp ist keine Browser-Vorschau verfügbar.</div>
                    )}
                  </div>
                  <div className="form-actions owner-vote-actions">
                    <a href={previewDocument.fileUrl} download={previewDocument.originalName}><button type="button">Download</button></a>
                    <button type="button" onClick={() => setPreviewDocument(null)}>Schließen</button>
                  </div>
                </section>
              </div>
            ) : null}

            {!isDraft && !isClosed && !currentVote ? (
              <section className="owner-vote-action-box">
                <div className="owner-vote-section-title">Ihre Stimmauswahl</div>
                <div className="owner-vote-options" role="radiogroup" aria-label="Stimmabgabe">
                  <button
                    type="button"
                    className={`owner-vote-option owner-vote-option-ja ${voteValue === "ja" ? "is-active" : ""}`}
                    onClick={() => setVoteValue("ja")}
                    disabled={!canVote || submitMutation.isPending}
                  >
                    Ja
                  </button>
                  <button
                    type="button"
                    className={`owner-vote-option owner-vote-option-nein ${voteValue === "nein" ? "is-active" : ""}`}
                    onClick={() => setVoteValue("nein")}
                    disabled={!canVote || submitMutation.isPending}
                  >
                    Nein
                  </button>
                  <button
                    type="button"
                    className={`owner-vote-option owner-vote-option-enthaltung ${voteValue === "enthaltung" ? "is-active" : ""}`}
                    onClick={() => setVoteValue("enthaltung")}
                    disabled={!canVote || submitMutation.isPending}
                  >
                    Enthaltung
                  </button>
                </div>

                <div className="form-field owner-vote-comment-field">
                  <label>Kommentar (optional)</label>
                  <textarea className="table-field" value={comment} onChange={(e) => setComment(e.target.value)} disabled={!canVote || submitMutation.isPending} rows={4} />
                </div>

                <div className="owner-vote-action-submit">
                  <button type="button" className={`owner-vote-submit-btn ${submitVariantClass}`} disabled={!canVote || submitMutation.isPending} onClick={() => submitMutation.mutate()}>
                    {submitMutation.isPending ? "Speichere..." : submitLabel}
                  </button>
                </div>
              </section>
            ) : null}

            {voteReceiptMessage ? (
              <section className={voteReceiptClassName} role="status" aria-live="polite">
                <span className={voteReceiptIconClassName} aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    {currentVote?.voteValue === "nein" ? (
                      <path d="M7 7 17 17M17 7 7 17" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                    ) : currentVote?.voteValue === "enthaltung" ? (
                      <path d="M6.5 12h11" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                    ) : (
                      <path d="m5 12 4.2 4.2L19 6.6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    )}
                  </svg>
                </span>
                <div>
                  <div className="owner-vote-success-notice-title">Stimmabgabe erfolgreich</div>
                  <p className="owner-vote-success-notice-text">{voteReceiptMessage}</p>
                  <p className="owner-vote-success-notice-text">{currentReturnRateText}</p>
                </div>
              </section>
            ) : null}


            {isClosed && !suppressClosedResultUntilReload ? (
              <section className="owner-vote-result-box" style={{ marginTop: 10 }}>
                <div className="owner-vote-result-heading">Abstimmungsergebnis</div>

                <div className="owner-vote-result-layout">
                  <div className="owner-vote-result-card owner-vote-result-chart-card">
                    <OwnerResultDonut
                      ja={Number(resultQuery.data?.stimmen.ja ?? 0)}
                      nein={Number(resultQuery.data?.stimmen.nein ?? 0)}
                      enthaltung={Number(resultQuery.data?.stimmen.enthaltung ?? 0)}
                    />
                  </div>

                  <div className={`owner-vote-result-card owner-vote-result-status-card ${resultOutcomeCardClass}`}>
                    <span className="owner-vote-result-status-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none">
                        {isResultRejected ? (
                          <path d="M7 7 17 17M17 7 7 17" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                        ) : (
                          <path d="m5 12 4.2 4.2L19 6.6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                        )}
                      </svg>
                    </span>
                    <strong className={`owner-vote-result-outcome ${resultOutcomeClass}`}>{resultOutcomeText}</strong>
                  </div>
                </div>
                {ownerResultVisible ? (
                  <div className="owner-vote-result-card owner-vote-result-list-card">
                    <div style={{ overflowX: "auto" }}>
                      <table>
                        <thead>
                          <tr>
                            <th>Eigentümer</th>
                            <th style={{ textAlign: "center" }}>Einheit</th>
                            <th style={{ textAlign: "center" }}>Stimmverhalten</th>
                            <th style={{ textAlign: "center" }}>Erfasst am</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ownerResultRows.map((row) => (
                            <tr key={row.ownerId}>
                              <td>{row.ownerLabel}</td>
                              <td style={{ textAlign: "center" }}>
                                {(row.einheiten ?? []).map((value) => formatUnitNumber4Digits(value)).join(" | ") || "-"}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                <span className={`badge ${voteBadgeClass(row.voteValue)}`}>{voteLabel(row.voteValue)}</span>
                              </td>
                              <td style={{ textAlign: "center" }}>{formatDateDE(row.voteReceivedAt)}</td>
                            </tr>
                          ))}
                          {!ownerResultRows.length ? <tr><td colSpan={4}>Keine Einzelstimmabgaben vorhanden.</td></tr> : null}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
              </section>
            ) : null}

            {isDraft ? (
              <section className="owner-vote-draft-notice" role="status" aria-live="polite">
                <div className="owner-vote-draft-notice-title">Umlaufbeschluss noch nicht gestartet</div>
                <p className="owner-vote-draft-notice-text">{"Der Umlaufbeschluss befindet sich aktuell im Entwurf. Eine Stimmabgabe ist erst nach dem Start möglich."}</p>
              </section>
            ) : null}

            {!isDraft && !isClosed && !canVote && !currentVote ? <div className="error-text">{"Abstimmung ist f\u00fcr diesen Zugang nicht mehr m\u00f6glich."}</div> : null}
            {submitError ? <div className="error-text">{submitError}</div> : null}

            <div className="form-actions owner-vote-actions">
              <Link to="/owner-login">
                <button type="button" className="owner-vote-back-btn">
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M14.8 6.2 9 12l5.8 5.8M9.2 12h9.8" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Zur Loginseite</span>
                </button>
              </Link>
            </div>
          </>
        ) : null}
      </section>
    </main>
  );
};

export default OwnerVotingPage;




























